import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  CheckCircle2,
  Info,
  Loader2,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  TablePagination,
  usePagination,
} from '@/components/ui/data-table';
import { FixingLoader, FixingOverlay } from '@/components/ui/FixingLoader';
import { LoadingAnimation } from '@/components/ui/LoadingAnimation';
import {
  formatElapsed,
  placementLoadingStage,
} from './placementReviewLoadingCopy';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  excludePlacements,
  runPlacementReview,
  type PlacementAmbiguousSharedSetDetail,
  type PlacementBlastRadiusConfirmationDetail,
  type PlacementExclusionSelection,
  type PlacementReviewRow,
} from '@/services/placementReviewService';

type CategoryKey =
  | 'mobile_app'
  | 'kids_content'
  | 'gaming'
  | 'gambling'
  | 'wrong_geo'
  | 'wrong_language';

const CATEGORY_LABELS: Record<CategoryKey, string> = {
  mobile_app: 'Mobile app',
  kids_content: 'Kids content',
  gaming: 'Gaming',
  gambling: 'Gambling',
  wrong_geo: 'Wrong geo',
  wrong_language: 'Wrong language',
};

const CATEGORY_KEYS = Object.keys(CATEGORY_LABELS) as CategoryKey[];

/** row.campaign_id + placement_type + placement — durable per-placement key
 * (unlike an id, this survives across re-pulls of the same review). */
function rowKey(row: PlacementReviewRow): string {
  return `${row.campaign_id}|${row.placement_type}|${row.placement}`;
}

function mechanismForCampaignType(campaignType: string): string {
  return campaignType === 'DISPLAY'
    ? 'direct negative placement criterion'
    : 'account-wide negative-placements shared set';
}

/**
 * CORE-P-02.b/c: POST /exclude answers 503 when placement exclusion is
 * switched off for the account ("Placement exclusion is switched off…";
 * older builds said "…PLACEMENT_EXCLUSION_ENABLED is not enabled"). That
 * is a state, not a failure, and a raw variable name means nothing to an
 * operator, so the Exclude button gets a calm line instead of a red
 * toast. The read no longer 503s for this (it returns
 * `exclusion_write_enabled: false`), so this is the secondary path, for a
 * write attempted anyway. Deliberately narrow: a bare "not enabled" is
 * NOT enough — the master-flag 503 says that too and must stay a failure.
 */
function isPlacementExclusionSwitchedOff(err: unknown): boolean {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  if (response?.status !== 503) return false;
  const detail = response.data?.detail;
  const text =
    typeof detail === 'string'
      ? detail
      : detail && typeof detail === 'object'
        ? JSON.stringify(detail)
        : '';
  return (
    /PLACEMENT_EXCLUSION_ENABLED/.test(text)
    || /placement exclusion is switched off/i.test(text)
  );
}

const EXCLUSION_SWITCHED_OFF_COPY =
  'Placement exclusion is switched off for this workspace. Ask an operator to turn it on to apply exclusions.';

function apiErrorDetail(err: unknown): string {
  const responseDetail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof responseDetail === 'string' && responseDetail.trim()) {
    return responseDetail.trim();
  }
  if (responseDetail) {
    try {
      return JSON.stringify(responseDetail);
    } catch {
      return String(responseDetail);
    }
  }
  const message = (err as { message?: string })?.message;
  return typeof message === 'string' && message.trim()
    ? message.trim()
    : 'No error detail was returned by the API.';
}

function parseBlastRadiusDetail(
  err: unknown,
): PlacementBlastRadiusConfirmationDetail | null {
  const response = (
    err as { response?: { status?: number; data?: { detail?: unknown } } }
  )?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail as
    | Partial<PlacementBlastRadiusConfirmationDetail>
    | undefined;
  if (!detail || detail.code !== 'blast_radius_confirmation_required') {
    return null;
  }
  const reasons = Array.isArray(detail.assessment?.reasons)
    ? detail.assessment.reasons.filter(
        (reason): reason is string => typeof reason === 'string',
      )
    : [];
  return {
    code: 'blast_radius_confirmation_required',
    assessment: {
      requires_confirmation: Boolean(detail.assessment?.requires_confirmation),
      reasons,
      summary: detail.assessment?.summary ?? {},
    },
    confirmation_phrase:
      typeof detail.confirmation_phrase === 'string'
      && detail.confirmation_phrase.trim()
        ? detail.confirmation_phrase
        : 'EXCLUDE PLACEMENTS',
  };
}

function parseAmbiguousSharedSetDetail(
  err: unknown,
): PlacementAmbiguousSharedSetDetail | null {
  const response = (
    err as { response?: { status?: number; data?: { detail?: unknown } } }
  )?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail as
    | Partial<PlacementAmbiguousSharedSetDetail>
    | undefined;
  if (!detail || detail.code !== 'ambiguous_shared_set') return null;
  const candidates = Array.isArray(detail.candidates) ? detail.candidates : [];
  return {
    code: 'ambiguous_shared_set',
    candidates,
    message:
      typeof detail.message === 'string' && detail.message.trim()
        ? detail.message
        : 'This account has more than one negative-placements shared set.',
  };
}

function metricCard(label: string, value: string, icon?: JSX.Element) {
  return (
    <div className="rounded border border-border bg-warm-bg p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export function PlacementReviewTab({ tenantId }: { tenantId: string }) {
  const [activeCategories, setActiveCategories] = useState<Set<CategoryKey>>(
    new Set(),
  );
  const [showExcluded, setShowExcluded] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  // CORE-P-02.c: set when a write came back 503 "switched off" — the
  // secondary signal; the primary one is the read's exclusion_write_enabled.
  const [writeRefusedSwitchedOff, setWriteRefusedSwitchedOff] = useState(false);
  const [blastRadius, setBlastRadius] =
    useState<PlacementBlastRadiusConfirmationDetail | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [ambiguousSharedSet, setAmbiguousSharedSet] =
    useState<PlacementAmbiguousSharedSetDetail | null>(null);
  const [selectedSharedSet, setSelectedSharedSet] = useState('');
  const queryClient = useQueryClient();

  const review = useQuery({
    // selectedSharedSet is keyed here too: once the operator picks a set
    // from the exclude-flow's ambiguity dialog, the review refetches
    // against that same set so already_excluded stops under-reporting
    // (see PlacementReviewResult.shared_set_ambiguous below).
    queryKey: ['google-ads-placement-review', tenantId, selectedSharedSet],
    queryFn: () =>
      runPlacementReview({
        tenantId,
        sharedSetResourceName: selectedSharedSet || undefined,
      }),
    enabled: Boolean(tenantId),
    refetchOnWindowFocus: false,
  });

  // Elapsed clock for the loading state. This request can run for minutes on a
  // large account, and a static spinner is indistinguishable from a hung tab —
  // an operator hit that on Mike Hammer Moving. Hook is unconditional (Rules of
  // Hooks / the CI lint:hooks gate); the interval only runs while fetching.
  const isRunning = review.isLoading || review.isFetching;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    setElapsedSeconds(0);
    const startedAt = Date.now();
    const id = window.setInterval(
      () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => window.clearInterval(id);
  }, [isRunning]);
  const loadingStage = placementLoadingStage(elapsedSeconds);

  const placements = review.data?.placements ?? [];
  const byKey = useMemo(() => {
    const map = new Map<string, PlacementReviewRow>();
    placements.forEach((row) => map.set(rowKey(row), row));
    return map;
  }, [placements]);

  const categoryFiltered =
    activeCategories.size === 0
      ? placements
      : placements.filter((row) =>
          Array.from(activeCategories).some((cat) => row[cat]),
        );
  const workingSet = showExcluded
    ? categoryFiltered
    : categoryFiltered.filter((row) => !row.already_excluded);

  const pagination = usePagination(workingSet.length, { defaultPageSize: 25 });
  const pageRows = pagination.slice(workingSet);

  const selectedRows = Array.from(selectedKeys)
    .map((key) => byKey.get(key))
    .filter((row): row is PlacementReviewRow => Boolean(row));

  const excludedCount = placements.filter((row) => row.already_excluded).length;
  const flaggedCount = placements.filter((row) => row.flagged).length;
  const hasPmaxRows = placements.some(
    (row) => row.campaign_type === 'PERFORMANCE_MAX',
  );
  const campaignsReadFailed = review.data?.campaigns_read_failed ?? [];

  const toggleCategory = (cat: CategoryKey) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
    pagination.setPage(1);
  };

  const toggleRow = (row: PlacementReviewRow) => {
    if (row.already_excluded) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const key = rowKey(row);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllOnPage = (select: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      pageRows
        .filter((row) => !row.already_excluded)
        .forEach((row) => {
          const key = rowKey(row);
          if (select) next.add(key);
          else next.delete(key);
        });
      return next;
    });
  };

  const excludeMutation = useMutation({
    mutationFn: (rows: PlacementReviewRow[]) =>
      excludePlacements({
        tenantId,
        selected: rows.map<PlacementExclusionSelection>((row) => ({
          campaign_id: row.campaign_id,
          campaign_type: row.campaign_type,
          placement: row.placement,
          placement_type: row.placement_type,
        })),
        confirmed: true,
        confirmationText: blastRadius ? confirmationText.trim() : undefined,
        sharedSetResourceName: selectedSharedSet || undefined,
      }),
    onSuccess: (result) => {
      if (result.dry_run) {
        toast.info(
          `Would exclude ${result.excluded} placement${result.excluded === 1 ? '' : 's'} `
          + '(dry run — nothing was written).',
        );
      } else {
        toast.success(
          `Excluded ${result.excluded} placement${result.excluded === 1 ? '' : 's'}`,
        );
      }
      if (result.failed_placements.length > 0) {
        const firstReason = result.failed_placements[0]?.reason;
        toast.error(
          `${result.failed_placements.length} placement(s) were rejected by `
          + 'Google Ads and not excluded.'
          + (firstReason ? ` (${firstReason})` : ''),
        );
      }
      if (result.blast_radius_required) {
        toast.info('Blast-radius safety gate confirmed for this exclusion.');
      }
      setConfirmOpen(false);
      setBlastRadius(null);
      setConfirmationText('');
      setAmbiguousSharedSet(null);
      setSelectedSharedSet('');
      setSelectedKeys(new Set());
      queryClient.invalidateQueries({
        queryKey: ['google-ads-placement-review', tenantId],
      });
    },
    onError: (err: unknown) => {
      if (isPlacementExclusionSwitchedOff(err)) {
        setWriteRefusedSwitchedOff(true);
        setConfirmOpen(false);
        setBlastRadius(null);
        setConfirmationText('');
        return;
      }
      const ambiguous = parseAmbiguousSharedSetDetail(err);
      if (ambiguous) {
        setAmbiguousSharedSet(ambiguous);
        setSelectedSharedSet(ambiguous.candidates[0]?.resource_name ?? '');
        return;
      }
      const blast = parseBlastRadiusDetail(err);
      if (blast) {
        setBlastRadius(blast);
        setConfirmationText('');
        return;
      }
      toast.error(`Exclusion failed: ${apiErrorDetail(err)}`);
    },
  });

  const handleConfirmOpenChange = (open: boolean) => {
    if (excludeMutation.isPending) return;
    setConfirmOpen(open);
    if (!open) {
      setBlastRadius(null);
      setConfirmationText('');
      setAmbiguousSharedSet(null);
      setSelectedSharedSet('');
    }
  };

  const confirmationMatches =
    blastRadius !== null
    && confirmationText.trim() === blastRadius.confirmation_phrase;

  const mechanismCounts = selectedRows.reduce<Record<string, number>>(
    (acc, row) => {
      const mechanism = mechanismForCampaignType(row.campaign_type);
      acc[mechanism] = (acc[mechanism] ?? 0) + 1;
      return acc;
    },
    {},
  );

  const errorDetail = review.isError ? apiErrorDetail(review.error) : '';
  // Read succeeded but writes are off for the account (P-02.c), or a write
  // was refused with the same meaning. Undefined (older backend) = enabled.
  const exclusionSwitchedOff =
    review.data?.exclusion_write_enabled === false || writeRefusedSwitchedOff;

  return (
    <div className="relative space-y-4">
      <FixingOverlay
        show={excludeMutation.isPending}
        label="Excluding placements…"
      />
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Review PMax/Display/Demand Gen placements and flag mobile apps,
              kids content, gaming, gambling, wrong-geo, and wrong-language
              inventory for exclusion.
            </div>
            <Button
              variant="outline"
              onClick={() => review.refetch()}
              disabled={review.isFetching}
            >
              {review.isFetching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {review.isLoading ? (
            <div
              className="flex flex-col items-center gap-3 py-10 text-center"
              role="status"
              aria-live="polite"
            >
              <LoadingAnimation size="md" />
              <div className="space-y-1">
                <div
                  className={
                    loadingStage.tone === 'warning'
                      ? 'text-sm font-medium text-destructive'
                      : 'text-sm font-medium'
                  }
                >
                  {loadingStage.headline}
                </div>
                <div className="mx-auto max-w-md text-xs text-muted-foreground">
                  {loadingStage.sub}
                </div>
              </div>
              <div className="font-mono text-xs tabular-nums text-muted-foreground">
                {formatElapsed(elapsedSeconds)} elapsed
              </div>
            </div>
          ) : review.isError ? (
            <div className="rounded border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              <div className="font-medium">Placement review failed.</div>
              <div className="mt-1 [overflow-wrap:anywhere]">{errorDetail}</div>
            </div>
          ) : review.data ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {metricCard(
                  'Campaigns read',
                  String(review.data.campaigns_read),
                )}
                {metricCard(
                  'Placements flagged',
                  String(flaggedCount),
                  <TriangleAlert className="h-3 w-3" />,
                )}
                {metricCard(
                  'Already excluded',
                  String(excludedCount),
                  <Ban className="h-3 w-3" />,
                )}
                {metricCard(
                  'Selected to exclude',
                  String(selectedRows.length),
                  <CheckCircle2 className="h-3 w-3" />,
                )}
              </div>

              {review.data.demand_gen_attempted
                && !review.data.demand_gen_supported && (
                <div className="flex items-start gap-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900 dark:text-amber-300">
                  <Info className="mt-0.5 h-4 w-4 flex-none" />
                  <div>
                    <div className="font-medium">
                      Demand Gen placement read did not succeed on this account.
                    </div>
                    <div className="mt-1 text-xs">
                      Google's support for placement reporting on Demand Gen
                      campaigns is unconfirmed — this account's Demand Gen
                      campaign(s) are excluded from the results above rather
                      than shown as zero placements.
                    </div>
                  </div>
                </div>
              )}

              {review.data.shared_set_ambiguous && (
                <div className="flex items-start gap-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900 dark:text-amber-300">
                  <TriangleAlert className="mt-0.5 h-4 w-4 flex-none" />
                  <div>
                    <div className="font-medium">
                      This account has more than one negative-placements list.
                    </div>
                    <div className="mt-1 text-xs">
                      "Already excluded" may under-report for PMax/Demand Gen
                      rows until a specific list is picked. Select any
                      placement and start an exclude — the picker that
                      appears there also fixes this review once you choose.
                    </div>
                  </div>
                </div>
              )}

              {campaignsReadFailed.length > 0 && (
                <div className="flex items-start gap-2 rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  <TriangleAlert className="mt-0.5 h-4 w-4 flex-none" />
                  <div>
                    <div className="font-medium">
                      Placement read failed for {campaignsReadFailed.length}
                      {' '}
                      campaign{campaignsReadFailed.length === 1 ? '' : 's'}.
                    </div>
                    <div className="mt-1 text-xs">
                      These campaigns are excluded from the results above —
                      not shown as having zero placements, which would be a
                      different fact. Refresh to retry:
                      {' '}
                      {campaignsReadFailed.map((c) => c.campaign_name || c.campaign_id).join(', ')}
                    </div>
                  </div>
                </div>
              )}

              {hasPmaxRows && (
                <div className="flex items-start gap-2 rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
                  <Info className="mt-0.5 h-4 w-4 flex-none" />
                  <div className="text-xs">
                    Performance Max placements show no spend/click metrics —
                    Google's API doesn't expose cost, clicks, or impressions
                    at this grain for PMax. This is a Google API limitation,
                    not a bug; Display/Demand Gen placements above do carry
                    metrics where available.
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {CATEGORY_KEYS.map((cat) => (
                    <Button
                      key={cat}
                      type="button"
                      variant={activeCategories.has(cat) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleCategory(cat)}
                      className="h-8 text-xs"
                    >
                      {CATEGORY_LABELS[cat]}
                    </Button>
                  ))}
                  {excludedCount > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowExcluded((prev) => !prev)}
                      className="h-8 gap-1 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-xs text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100"
                    >
                      <Ban className="h-3 w-3" />
                      {excludedCount} excluded — {showExcluded ? 'hide' : 'show'}
                    </Button>
                  )}
                </div>
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={
                    !selectedRows.length || excludeMutation.isPending || exclusionSwitchedOff
                  }
                >
                  Exclude selected
                </Button>
              </div>
              {exclusionSwitchedOff && (
                <p
                  role="status"
                  data-testid="placement-exclusion-switched-off"
                  className="text-right text-xs text-muted-foreground"
                >
                  {EXCLUSION_SWITCHED_OFF_COPY}
                </p>
              )}

              <div className="overflow-x-auto rounded border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={
                            pageRows.length > 0
                            && pageRows
                              .filter((row) => !row.already_excluded)
                              .every((row) => selectedKeys.has(rowKey(row)))
                          }
                          onCheckedChange={(checked) =>
                            toggleAllOnPage(Boolean(checked))
                          }
                        />
                      </TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Placement</TableHead>
                      <TableHead>Categories</TableHead>
                      <TableHead>Reasoning</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                          No placements match the current filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      pageRows.map((row) => {
                        const key = rowKey(row);
                        return (
                          <TableRow key={key}>
                            <TableCell>
                              <Checkbox
                                checked={selectedKeys.has(key)}
                                disabled={row.already_excluded}
                                onCheckedChange={() => toggleRow(row)}
                              />
                            </TableCell>
                            <TableCell className="max-w-[220px] truncate">
                              {row.campaign_name}
                            </TableCell>
                            <TableCell className="text-xs">{row.campaign_type}</TableCell>
                            <TableCell className="max-w-[260px] truncate">
                              <div>{row.display_name || row.placement}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {row.placement}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {CATEGORY_KEYS.filter((cat) => row[cat]).map(
                                  (cat) => (
                                    <Badge
                                      key={cat}
                                      variant="outline"
                                      className="border-coral/40 bg-coral-light/40 text-coral-dark text-[11px]"
                                    >
                                      {CATEGORY_LABELS[cat]}
                                    </Badge>
                                  ),
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[260px] text-xs text-muted-foreground">
                              {row.reasoning}
                            </TableCell>
                            <TableCell>
                              {row.already_excluded ? (
                                <Badge
                                  variant="outline"
                                  className="border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 text-[11px]"
                                >
                                  Excluded
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">Active</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {workingSet.length > 0 && (
                <TablePagination
                  page={pagination.page}
                  pageCount={pagination.pageCount}
                  pageSize={pagination.pageSize}
                  totalRows={workingSet.length}
                  onPageChange={pagination.setPage}
                  onPageSizeChange={pagination.setPageSize}
                />
              )}
            </>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={handleConfirmOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exclude selected placements?</DialogTitle>
            <DialogDescription>
              This removes {selectedRows.length} selected placement
              {selectedRows.length === 1 ? '' : 's'} from active serving.
              {' '}
              {Object.entries(mechanismCounts)
                .map(([mechanism, count]) => `${count} via ${mechanism}`)
                .join('; ')}
              . This is a Google Ads write unless the backend deploy is
              running in dry-run mode, and removing a placement from active
              serving is effectively irreversible — Google may re-serve the
              same URL later, but the impressions/clicks lost in between are
              gone.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-56 overflow-auto rounded border border-border bg-warm-bg p-3 text-sm">
            {selectedRows.map((row) => (
              <div key={rowKey(row)} className="flex justify-between gap-3 py-1">
                <span className="truncate">{row.display_name || row.placement}</span>
                <span className="text-xs text-muted-foreground">
                  {row.campaign_type}
                </span>
              </div>
            ))}
          </div>
          {selectedRows.some((row) => row.campaign_type !== 'DISPLAY') && (
            <div className="rounded border border-border bg-warm-bg p-3 text-xs text-muted-foreground">
              Performance Max / Demand Gen exclusions write to an
              account-wide negative-placements shared set, not just this
              campaign — once that set is attached to more than one
              campaign, it affects every campaign it's attached to.
              (Display's direct negative criterion above stays
              campaign-scoped.)
            </div>
          )}
          {excludeMutation.isPending && (
            <div className="flex items-center gap-2 rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
              <FixingLoader iconOnly />
              Excluding — this writes to Google Ads and can take a few
              seconds. Please don't close this dialog.
            </div>
          )}
          {ambiguousSharedSet && (
            <div className="space-y-3 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900 dark:text-amber-300">
              <div className="flex items-center gap-2 font-medium">
                <TriangleAlert className="h-4 w-4 flex-none" />
                Which negative-placements shared set?
              </div>
              <div className="text-xs">{ambiguousSharedSet.message}</div>
              <select
                value={selectedSharedSet}
                onChange={(event) => setSelectedSharedSet(event.target.value)}
                className="w-full rounded border border-border bg-card p-2 text-sm"
              >
                {ambiguousSharedSet.candidates.map((candidate) => (
                  <option key={candidate.resource_name} value={candidate.resource_name}>
                    {candidate.name || candidate.resource_name}
                    {' '}
                    ({candidate.member_count.toLocaleString()} existing member(s))
                  </option>
                ))}
              </select>
            </div>
          )}
          {blastRadius && (
            <div className="space-y-3 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900 dark:text-amber-300">
              <div className="flex items-center gap-2 font-medium">
                <TriangleAlert className="h-4 w-4 flex-none" />
                Additional confirmation required (blast-radius safety gate)
              </div>
              {blastRadius.assessment.reasons.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-xs">
                  {blastRadius.assessment.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
              <div className="space-y-1">
                <Input
                  value={confirmationText}
                  onChange={(event) => setConfirmationText(event.target.value)}
                  placeholder={blastRadius.confirmation_phrase}
                  className="bg-card"
                  autoFocus
                />
                <div className="text-xs text-amber-900 dark:text-amber-300/80">
                  Type{' '}
                  <code className="rounded bg-card px-1 py-0.5 text-[11px]">
                    {blastRadius.confirmation_phrase}
                  </code>{' '}
                  exactly, then exclude again.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleConfirmOpenChange(false)}
              disabled={excludeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => excludeMutation.mutate(selectedRows)}
              disabled={
                excludeMutation.isPending
                || exclusionSwitchedOff
                || (blastRadius !== null && !confirmationMatches)
                || (ambiguousSharedSet !== null && !selectedSharedSet)
              }
            >
              {excludeMutation.isPending && <FixingLoader iconOnly />}
              {excludeMutation.isPending ? 'Excluding…' : 'Confirm and exclude'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
